import type { Locale } from '@seek/melways-sites';
import { Stack } from 'braid-design-system';

import {
  ApplicationAttachmentType,
  type ApplicationAttachmentV2,
  type ApplicationNoteV2,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import { AttachmentItem } from '../AttachmentItem/AttachmentItem';
import type { NotesAttachmentHistoryQuery } from '../NotesAttachmentHistory.hirer.generated';
import { NoteItem } from '../NotesItem/NoteItem';

interface Props {
  notesAttachmentsData: NotesAttachmentHistoryQuery | undefined;
  locale: Locale;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  jobId: string;
  applicationCorrelationId: string;
  onDeleteNote: (noteItem: ApplicationNoteV2) => Promise<'done' | 'error'>;
  onDeleteAttachment: (
    attachmentItem: ApplicationAttachmentV2,
  ) => Promise<'done' | 'error'>;
  userId: string;
  jobApplicationId: string;
  seekCandidateId: string;
  candidateId: string;
}
export const NotesAttachmentList = ({
  notesAttachmentsData,
  locale,
  isVerifiedId,
  jobId,
  applicationCorrelationId,
  onDeleteNote,
  onDeleteAttachment,
  userId,
  jobApplicationId,
  seekCandidateId,
  candidateId,
}: Props) => {
  const attachmentData =
    notesAttachmentsData?.applicationAttachmentsV2.__typename ===
    'ApplicationAttachmentsV2ResponseSuccess'
      ? notesAttachmentsData.applicationAttachmentsV2.result
      : [];

  const notesData =
    notesAttachmentsData?.applicationNotesV2.__typename ===
    'ApplicationNotesResponseSuccess'
      ? notesAttachmentsData.applicationNotesV2.result
      : [];

  const sortedAttachmentsNotes = [...attachmentData, ...notesData].sort(
    (a, b) => {
      const dateA =
        (a.__typename === 'ApplicationAttachmentV2' && a.uploadedDate) ||
        (a.__typename === 'ApplicationNoteV2' && a.createdDate);

      const dateB =
        (b.__typename === 'ApplicationAttachmentV2' && b.uploadedDate) ||
        (b.__typename === 'ApplicationNoteV2' && b.createdDate);

      if (dateA && dateB) {
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }
      return 0;
    },
  );

  return (
    <Stack space="small">
      {sortedAttachmentsNotes.map(
        (attachment: ApplicationAttachmentV2 | ApplicationNoteV2) => {
          const attachmentItem =
            attachment.__typename === 'ApplicationAttachmentV2'
              ? attachment
              : undefined;

          const noteItem =
            attachment.__typename === 'ApplicationNoteV2'
              ? attachment
              : undefined;

          if (
            (!attachmentItem && !noteItem) ||
            (attachmentItem &&
              attachmentItem.attachmentType !== ApplicationAttachmentType.OTHER)
          ) {
            return <></>;
          }

          if (attachmentItem) {
            return (
              <AttachmentItem
                key={attachmentItem.blobId}
                attachmentItem={attachmentItem}
                locale={locale}
                isVerifiedId={isVerifiedId}
                jobId={jobId}
                applicationCorrelationId={applicationCorrelationId}
                onDelete={onDeleteAttachment}
                jobApplicationId={jobApplicationId}
                seekCandidateId={seekCandidateId}
                candidateId={candidateId}
              />
            );
          } else if (noteItem) {
            return (
              <NoteItem
                key={noteItem.id}
                noteItem={noteItem!}
                locale={locale}
                onDelete={onDeleteNote}
                canDelete={userId === noteItem.createdUserId}
              />
            );
          }
        },
      )}
    </Stack>
  );
};
